/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CBadge,
  CButton,
  CSpinner,
  CAlert,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CFormInput,
  CFormLabel,
  CFormSelect
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { 
  cilArrowLeft, 
  cilLayers,
  cilCalendar,
  cilUser,
  cilCheckCircle,
  cilXCircle,
  cilWarning,
  cilClock,
  cilTag,
  cilDescription,
  cilTruck,
  cilHouse,
  cilLocationPin,
  cilShieldAlt,
  cilCash,
  cilCart,
  cilPencil
} from '@coreui/icons';
import { getOrderDetailsRequest, updateSwapShipmentRequest, updateSwapStockRequest } from '../../store/order/orderActions';
import { getCurrencyRequestAction } from '../../store/currency/currencyActions';
import { getOrderDropdown } from '../../services/endpoints';
import './OrderDetails.scss';
import { formatScaledAmount, toScaledInteger } from '../../helpers/quantity';

function OrderDetails(props) {
  const {
    getOrderDetailsRequest,
    updateSwapStockRequest,
    updateSwapShipmentRequest,
    orderDetailsData,
    orderDetailsError,
    isLoading,
    currencyList,
    getCurrencyRequestAction,
  } = props;
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  };
  const [activeTab, setActiveTab] = useState('product');
  const [orderInfo, setOrderInfo] = useState(null);
  const [dropdownCurrencies, setDropdownCurrencies] = useState([]);
  const [shipmentSwapModalVisible, setShipmentSwapModalVisible] = useState(false);
  const [stockSwapModalVisible, setStockSwapModalVisible] = useState(false);
  const [selectedShipmentRow, setSelectedShipmentRow] = useState(null);
  const [selectedStockRow, setSelectedStockRow] = useState(null);
  const [shipmentSwapForm, setShipmentSwapForm] = useState({
    ShipmentId: '',
    StockId: '',
    ShipmentSwapQuantity: '',
    ShipmentSwapRollCount: '',
  });
  const [stockSwapForm, setStockSwapForm] = useState({
    StockId: '',
    ShipmentId: '',
    StockSwapQuantity: '',
    StockSwapRollCount: '',
  });

  useEffect(() => {
    // Load currencies (once)
    getCurrencyRequestAction({});

    // Load dropdown currencies (once)
    (async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const sessionUserId = localStorage.getItem('sessionUserId');
        const userId = sessionUserId || userData?.sessionUserId;
        const payload = {
          SessionUserId: userId,
          OrderId: '',
          IncludeOrders: false,
          IncludeProducts: false,
          IncludeWeaveColors: false,
        };
        const response = await getOrderDropdown(payload);
        const data = response?.data || {};
        const currenciesRaw = data.Currencies || [];
        const normalized = currenciesRaw.map(c => ({
          PrdCurrencyGuid: c.PrdCurrencyGuid || c.CostCurrencyGuid || c.SalesCurrencyGuid,
          CurrencyName: c.CurrencyName || c.CostCurrencyName || c.SalesCurrencyName,
          CurrencyCode: c.CurrencyCode || c.CostCurrencyCode || c.SalesCurrencyCode,
        })).filter(c => c.PrdCurrencyGuid);
        setDropdownCurrencies(normalized);
      } catch (err) {
        setDropdownCurrencies([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (id) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const sessionUserIdValue = sessionUserId || userData?.sessionUserId;

      if (sessionUserIdValue && sessionUserIdValue !== 'undefined') {
        const requestData = {
          SessionUserId: String(sessionUserIdValue),
          OrderGuid: String(id)
        };
        getOrderDetailsRequest(requestData);
      }
    }
  }, [id]);

  useEffect(() => {
    if (location.state?.order) {
      setOrderInfo(location.state.order);
    }
  }, [location.state]);

  const handleGoBack = () => {
    navigate('/orders');
  };

  const getSessionUserIdValue = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionUserId = localStorage.getItem('sessionUserId');
    return String(sessionUserId || userData?.sessionUserId || '');
  };

  const normalizeIntegerString = (value) => {
    const parsed = Number.parseInt(String(value || '').trim(), 10);
    return Number.isNaN(parsed) ? '0' : String(parsed);
  };

  const normalizeTransferQuantityString = (value) => {
    const normalized = String(value || '').trim().replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isNaN(parsed) ? '0' : String(Math.round(parsed * 100));
  };

  const parseIntOrZero = (value) => {
    const parsed = Number.parseInt(String(value || '').trim(), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const openShipmentSwapModal = (shipment) => {
    const defaultTargetStockId = filteredStockDetails?.[0]?.StockGuid || '';

    setSelectedShipmentRow(shipment || null);
    setShipmentSwapForm({
      ShipmentId: shipment?.ShipmentGuid || '',
      StockId: defaultTargetStockId,
      ShipmentSwapQuantity: '',
      ShipmentSwapRollCount: '',
    });
    setShipmentSwapModalVisible(true);
  };

  const openStockSwapModal = (stock) => {
    const defaultTargetShipmentId = filteredShipmentDetails?.[0]?.ShipmentGuid || '';

    setSelectedStockRow(stock || null);
    setStockSwapForm({
      StockId: stock?.StockGuid || '',
      ShipmentId: defaultTargetShipmentId,
      StockSwapQuantity: '',
      StockSwapRollCount: '',
    });
    setStockSwapModalVisible(true);
  };

  const handleShipmentSwapSubmit = () => {
    const sessionUserIdValue = getSessionUserIdValue();
    if (!sessionUserIdValue) return;

    const requestData = {
      SessionUserId: sessionUserIdValue,
      ShipmentId: String(shipmentSwapForm.ShipmentId || ''),
      StockId: String(shipmentSwapForm.StockId || ''),
      ShipmentSwapQuantity: normalizeTransferQuantityString(shipmentSwapForm.ShipmentSwapQuantity),
      ShipmentSwapRollCount: normalizeIntegerString(shipmentSwapForm.ShipmentSwapRollCount),
      OrderGuid: String(id || ''),
    };

    updateSwapShipmentRequest(requestData);
    setShipmentSwapModalVisible(false);
  };

  const handleStockSwapSubmit = () => {
    const sessionUserIdValue = getSessionUserIdValue();
    if (!sessionUserIdValue) return;

    const requestData = {
      SessionUserId: sessionUserIdValue,
      StockId: String(stockSwapForm.StockId || ''),
      ShipmentId: String(stockSwapForm.ShipmentId || ''),
      StockSwapQuantity: normalizeTransferQuantityString(stockSwapForm.StockSwapQuantity),
      StockSwapRollCount: normalizeIntegerString(stockSwapForm.StockSwapRollCount),
      OrderGuid: String(id || ''),
    };

    updateSwapStockRequest(requestData);
    setStockSwapModalVisible(false);
  };

  const isShipmentSwapDisabled =
    !shipmentSwapForm.ShipmentId ||
    !shipmentSwapForm.StockId ||
    (parseIntOrZero(shipmentSwapForm.ShipmentSwapQuantity) <= 0 &&
      parseIntOrZero(shipmentSwapForm.ShipmentSwapRollCount) <= 0);

  const isStockSwapDisabled =
    !stockSwapForm.StockId ||
    !stockSwapForm.ShipmentId ||
    (parseIntOrZero(stockSwapForm.StockSwapQuantity) <= 0 &&
      parseIntOrZero(stockSwapForm.StockSwapRollCount) <= 0);

  const formatDate = (dateTime) => {
    if (!dateTime || dateTime === "0") return '-';
    
    const dateStr = String(dateTime).trim();
    
    // Handle DDMMYYYY format (e.g., "23112026")
    if (dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
      const day = dateStr.substring(0, 2);
      const month = dateStr.substring(2, 4);
      const year = dateStr.substring(4, 8);
      
      const date = new Date(`${year}-${month}-${day}`);
      
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
    }
    
    if (dateStr.length === 16) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      const hour = dateStr.substring(8, 10);
      const minute = dateStr.substring(10, 12);
      const second = dateStr.substring(12, 14);
      
      const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
      
      if (!isNaN(date.getTime())) {
        const utcPlus3Date = new Date(date.getTime() + (3 * 60 * 60 * 1000));
        
        return utcPlus3Date.toLocaleDateString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      }
    }
    
    if (dateStr.length === 14) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      const hour = dateStr.substring(8, 10);
      const minute = dateStr.substring(10, 12);
      const second = dateStr.substring(12, 14);
      
      const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
      
      if (!isNaN(date.getTime())) {
        const utcPlus3Date = new Date(date.getTime() + (3 * 60 * 60 * 1000));
        
        return utcPlus3Date.toLocaleDateString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      }
    }
    
    try {
      const date = new Date(dateTime);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      }
    } catch (error) {
      console.error('Date formatting error:', error);
    }
    
    return '-';
  };

  const formatQuantity = (quantity) => {
    if (!quantity || quantity === 0) return '-';
    return new Intl.NumberFormat('tr-TR').format(quantity);
  };

  const formatInsertDatetime = (dateTime) => {
    if (!dateTime || dateTime === 0) return '-';
    
    const dateStr = String(dateTime).trim();
    
    // Handle YYYYMMDDHHMMSSSS format (e.g., 2025072913324801)
    if (dateStr.length === 16 && /^\d{16}$/.test(dateStr)) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      const hour = dateStr.substring(8, 10);
      const minute = dateStr.substring(10, 12);
      const second = dateStr.substring(12, 14);
      
      const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
      
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      }
    }
    
    return formatDate(dateTime);
  };

  // Get currency symbol based on currency name or code
  const getCurrencySymbol = (currencyName) => {
    if (!currencyName) return '';
    
    const currency = currencyName.toLowerCase();
    
    if (currency.includes('euro') || currency.includes('eur')) return '€';
    if (currency.includes('dolar') || currency.includes('dollar') || currency.includes('usd')) return '$';
    if (currency.includes('türk') || currency.includes('turk') || currency.includes('try') || currency.includes('tl') || currency.includes('lira')) return '₺';
    if (currency.includes('pound') || currency.includes('sterlin') || currency.includes('gbp')) return '£';
    if (currency.includes('yen') || currency.includes('jpy')) return '¥';
    if (currency.includes('yuan') || currency.includes('cny')) return '¥';
    if (currency.includes('rupee') || currency.includes('inr')) return '₹';
    if (currency.includes('ruble') || currency.includes('rub')) return '₽';
    
    return currencyName;
  };

  // Resolve currency symbol by GUID using currency list (Redux + Dropdown fallback)
  const getCurrencySymbolByGuid = (guid) => {
    if (!guid) return '';
    const allCurrencies = [ ...(currencyList || []), ...dropdownCurrencies ];
    const found = allCurrencies.find(c => c.PrdCurrencyGuid === guid);
    if (!found) return '';
    
    const code = (found.CurrencyCode || '').toLowerCase();
    const name = (found.CurrencyName || '').toLowerCase();
    
    if (code === 'eur' || code === 'euro') return '€';
    if (code === 'usd' || code === 'dollar') return '$';
    if (code === 'try' || code === 'tl' || code === 'turkish lira') return '₺';
    if (code === 'gbp' || code === 'pound') return '£';
    if (code === 'jpy' || code === 'yen') return '¥';
    if (code === 'cny' || code === 'yuan') return '¥';
    if (code === 'inr' || code === 'rupee') return '₹';
    if (code === 'rub' || code === 'ruble') return '₽';
    
    if (name.includes('euro') || name.includes('eur')) return '€';
    if (name.includes('dolar') || name.includes('dollar') || name.includes('usd')) return '$';
    if (name.includes('türk') || name.includes('turk') || name.includes('try') || name.includes('tl') || name.includes('lira')) return '₺';
    if (name.includes('pound') || name.includes('sterlin') || name.includes('gbp')) return '£';
    if (name.includes('yen') || name.includes('jpy')) return '¥';
    if (name.includes('yuan') || name.includes('cny')) return '¥';
    if (name.includes('rupee') || name.includes('inr')) return '₹';
    if (name.includes('ruble') || name.includes('rub')) return '₽';
    
    return found.CurrencyName || '';
  };

  // Format price with currency symbol (using GUID or fallback to name)
  const formatPrice = (price, guid, fallbackCurrencyName) => {
    if (price === null || price === undefined) return '-';
    const numericPrice = Number(price);
    if (Number.isNaN(numericPrice)) return '-';
    const formattedPrice = (numericPrice / 100).toFixed(2);

    let symbol = getCurrencySymbolByGuid(guid);
    if (!symbol && fallbackCurrencyName) {
      symbol = getCurrencySymbol(fallbackCurrencyName);
    }
    if (!symbol || String(symbol).trim() === '') {
      symbol = '₺';
    }

    return `${formattedPrice} ${symbol}`;
  };

  const getOrderStatusBadge = (status) => {
    const statusMap = {
      'Active': { color: 'success', text: t('active') || 'Aktif', icon: cilCheckCircle },
      'Pending': { color: 'warning', text: t('pending') || 'Beklemede', icon: cilClock },
      'Completed': { color: 'info', text: t('completed') || 'Tamamlandı', icon: cilCheckCircle },
      'Cancelled': { color: 'danger', text: t('cancelled') || 'İptal', icon: cilXCircle }
    };
    
    const statusInfo = statusMap[status] || { color: 'secondary', text: status, icon: cilWarning };
    return (
      <CBadge color={statusInfo.color} shape="rounded-pill" className="order-status-badge">
        <CIcon icon={statusInfo.icon} size="sm" className="me-1" />
        {statusInfo.text}
      </CBadge>
    );
  };

  const getShipmentStatusBadge = (status) => {
    const statusMap = {
      'Delivered': { color: 'success', text: t('delivered') || 'Teslim Edildi', icon: cilCheckCircle },
      'In Transit': { color: 'info', text: t('inTransit') || 'Yolda', icon: cilTruck },
      'Pending': { color: 'warning', text: t('pending') || 'Beklemede', icon: cilClock },
      'Cancelled': { color: 'danger', text: t('cancelled') || 'İptal', icon: cilXCircle }
    };
    
    const statusInfo = statusMap[status] || { color: 'secondary', text: status, icon: cilWarning };
    return (
      <CBadge color={statusInfo.color} shape="rounded-pill" className="status-badge">
        <CIcon icon={statusInfo.icon} size="sm" className="me-1" />
        {statusInfo.text}
      </CBadge>
    );
  };

  const getStockStatusBadge = (status) => {
    const statusMap = {
      'Available': { color: 'success', text: t('available') || 'Mevcut', icon: cilCheckCircle },
      'Reserved': { color: 'warning', text: t('reserved') || 'Rezerve', icon: cilShieldAlt },
      'Out of Stock': { color: 'danger', text: t('outOfStock') || 'Stokta Yok', icon: cilXCircle },
      'Low Stock': { color: 'info', text: t('lowStock') || 'Düşük Stok', icon: cilWarning }
    };
    
    const statusInfo = statusMap[status] || { color: 'secondary', text: status, icon: cilWarning };
    return (
      <CBadge color={statusInfo.color} shape="rounded-pill" className="status-badge">
        <CIcon icon={statusInfo.icon} size="sm" className="me-1" />
        {statusInfo.text}
      </CBadge>
    );
  };

  const currentOrder = orderDetailsData?.OrderDetail?.[0] || orderInfo;
  const productDetail = orderDetailsData?.ProductDetail?.[0];
  const orderStatusDetail = orderDetailsData?.OrderStatusDetail?.[0];
  const shipmentDetails = orderDetailsData?.ShipmentDetail || [];
  const stockDetails = orderDetailsData?.StockDetail || [];
  const hasMeaningfulValue = (obj, ignoreKeys = []) => {
    if (!obj) return false;
    return Object.entries(obj).some(([key, value]) => {
      if (ignoreKeys.includes(key)) return false;
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'number') return value !== 0;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') return Object.keys(value).length > 0;
      return Boolean(value);
    });
  };

  const filteredShipmentDetails = shipmentDetails.filter((s) => hasMeaningfulValue(s, ['InsertUserGuid', 'InsertDatetime']));
  const filteredStockDetails = stockDetails.filter((s) => hasMeaningfulValue(s, ['InsertUserGuid', 'InsertDatetime']));

  const getQuantityTotalsByUnit = (items, quantityKey, unitKey) => {
    const totals = new Map();

    items.forEach((item) => {
      const unitName = String(item?.[unitKey] || tr('unitless', 'Birimsiz')).trim();
      const normalizedUnit = unitName.toLocaleLowerCase('tr-TR');
      const current = totals.get(normalizedUnit) || { unitName, quantity: 0 };
      current.quantity += toScaledInteger(item?.[quantityKey]);
      totals.set(normalizedUnit, current);
    });

    return Array.from(totals.values());
  };

  const shipmentQuantityTotals = getQuantityTotalsByUnit(
    filteredShipmentDetails,
    'ProductShippedQuantity',
    'ShipmentPrdUnitName'
  );
  const stockQuantityTotals = getQuantityTotalsByUnit(
    filteredStockDetails,
    'ProductStockQuantity',
    'UnitName'
  );
  const shipmentRollTotal = filteredShipmentDetails.reduce(
    (total, shipment) => total + parseIntOrZero(shipment.RollCount),
    0
  );
  const stockRollTotal = filteredStockDetails.reduce(
    (total, stock) => total + parseIntOrZero(stock.RollCount),
    0
  );

  const stockTargetOptions = filteredStockDetails.map((stock) => ({
    value: stock.StockGuid,
    label: `${stock.StockPlace || '-'} • ${tr('quantity', 'Miktar')}: ${formatScaledAmount(stock.ProductStockQuantity) || '0,00'} • ${tr('rollCount', 'Top')}: ${stock.RollCount || 0}`,
  }));

  const shipmentTargetOptions = filteredShipmentDetails.map((shipment) => ({
    value: shipment.ShipmentGuid,
    label: `${shipment.BillingCompany || '-'} • ${tr('quantity', 'Miktar')}: ${formatScaledAmount(shipment.ProductShippedQuantity) || '0,00'} • ${tr('rollCount', 'Top')}: ${shipment.RollCount || 0}`,
  }));

  const productCount = Array.isArray(orderDetailsData?.ProductDetail) ? orderDetailsData.ProductDetail.length : 0;
  const orderStatusCount = Array.isArray(orderDetailsData?.OrderStatusDetail) ? orderDetailsData.OrderStatusDetail.length : 0;
  const shipmentCount = filteredShipmentDetails.length;
  const stockCount = filteredStockDetails.length;
  const showShipmentLotCol = filteredShipmentDetails.some((s) => s.Lot && String(s.Lot).trim() !== '');
  const showShipmentDescriptionCol = filteredShipmentDetails.some((s) => s.Description && String(s.Description).trim() !== '');
  const showStockLotCol = filteredStockDetails.some((s) => s.Lot && String(s.Lot).trim() !== '');
  const showStockDescriptionCol = filteredStockDetails.some((s) => s.Description && String(s.Description).trim() !== '');

  if (isLoading) {
    return (
      <div className="order-details-loading">
        <CSpinner color="primary" className="order-details-spinner" />
        <p className="mt-3 text-muted">{t('loadingOrderDetails') || 'Sipariş detayları yükleniyor...'}</p>
      </div>
    );
  }

  if (orderDetailsError) {
    return (
      <div className="order-details-error">
        <CButton
          color="secondary"
          variant="outline"
          onClick={handleGoBack}
          className="mb-3"
        >
          <CIcon icon={cilArrowLeft} className="me-2" />
          {t('goBack') || 'Geri Dön'}
        </CButton>
        <CAlert color="danger" className="error-alert">
          <CIcon icon={cilXCircle} className="me-2" />
          {t('orderLoadError') || 'Sipariş bilgileri yüklenirken bir hata oluştu.'}
        </CAlert>
      </div>
    );
  }

  if (!currentOrder) {
    return (
      <div className="order-details-not-found">
        <CButton
          color="secondary"
          variant="outline"
          onClick={handleGoBack}
          className="mb-3"
        >
          <CIcon icon={cilArrowLeft} className="me-2" />
          {t('goBack') || 'Geri Dön'}
        </CButton>
        <CAlert color="warning" className="warning-alert">
          <CIcon icon={cilWarning} className="me-2" />
          {t('orderNotFound') || 'Sipariş bulunamadı.'}
        </CAlert>
      </div>
    );
  }

  // Extract currency GUIDs from the order data
  const costCurrencyGuid = currentOrder.CostCurrencyGuid || null;
  const salesCurrencyGuid = currentOrder.SalesCurrencyGuid || null;
  const costCurrencySymbol = getCurrencySymbolByGuid(costCurrencyGuid) || getCurrencySymbol(currentOrder.CostCurrencyName);
  const salesCurrencySymbol = getCurrencySymbolByGuid(salesCurrencyGuid) || getCurrencySymbol(currentOrder.SalesCurrencyName) || '₺';

  return (
    <div className="order-details-container">
      {/* Header */}
      <div className="order-details-header">
        <div className="header-left">
          <CButton
            color="secondary"
            variant="outline"
            onClick={handleGoBack}
            className="back-button"
          >
            <CIcon icon={cilArrowLeft} className="me-2" />
            {t('goBack') || 'Geri Dön'}
          </CButton>
          <div className="header-title">
            <h3>{t('orderDetails') || 'Sipariş Detayları'}</h3>
            <span className="header-subtitle">
              {currentOrder.OrderNo || currentOrder.ProductCodeForCustomer || currentOrder.OrderGuid}
            </span>
          </div>
        </div>
      </div>

      {/* Order Summary Card */}
      <CCard className="order-summary-card">
        <CCardBody>
          <CRow>
            <CCol lg={8} className="order-info-section">
              <div className="order-header">
                <div className="order-title-section">
                  <h4 className="order-title">
                    <CIcon icon={cilLayers} className="me-2" />
                    {currentOrder.OrderNo || currentOrder.ProductCodeForCustomer || t('order')}
                  </h4>
                  <p className="order-id">{t('orderNo')}: {currentOrder.OrderNo || currentOrder.OrderGuid}</p>
                </div>
                <div className="order-status-section">
                  {getOrderStatusBadge(currentOrder.Name)}
                </div>
              </div>
              
              <div className="order-basic-info">
                <div className="info-item">
                  <CIcon icon={cilUser} className="info-icon" />
                  <span className="info-text">
                    <strong>{t('customer')}:</strong> {currentOrder.CustomerName}
                  </span>
                </div>
                <div className="info-item">
                  <CIcon icon={cilTag} className="info-icon" />
                  <span className="info-text">
                    <strong>{t('customerCode')}:</strong> {currentOrder.CustomerCode || '-'}
                  </span>
                </div>
                <div className="info-item">
                  <CIcon icon={cilDescription} className="info-icon" />
                  <span className="info-text">
                    <strong>{t('paymentTerm')}:</strong> {currentOrder.PaymentTerm || '-'}
                  </span>
                </div>
                <div className="info-item">
                  <CIcon icon={cilCalendar} className="info-icon" />
                  <span className="info-text">
                    <strong>{t('orderDate')}:</strong> {formatDate(currentOrder.OrderDatetime)}
                  </span>
                </div>
                <div className="info-item">
                  <CIcon icon={cilClock} className="info-icon" />
                  <span className="info-text">
                    <strong>{t('deadline')}:</strong> {formatDate(currentOrder.OrderDeadlineDatetime)}
                  </span>
                </div>
                {/* Unit removed as requested */}
                <div className="info-item">
                  <CIcon icon={cilCalendar} className="info-icon" />
                  <span className="info-text">
                    <strong>{t('productionStart')}:</strong> {formatDate(currentOrder.ProductionStartDatetime)}
                  </span>
                </div>
                <div className="info-item">
                  <CIcon icon={cilCalendar} className="info-icon" />
                  <span className="info-text">
                    <strong>{t('productionEnd')}:</strong> {formatDate(currentOrder.ProductionEndDatetime)}
                  </span>
                </div>
                <div className="info-item">
                  <CIcon icon={cilCalendar} className="info-icon" />
                  <span className="info-text">
                    <strong>{t('insertDate')}:</strong> {formatInsertDatetime(currentOrder.InsertDatetime)}
                  </span>
                </div>
                {currentOrder.Note && (
                  <div className="info-item">
                    <CIcon icon={cilDescription} className="info-icon" />
                    <span className="info-text">
                      <strong>{t('note')}:</strong> {currentOrder.Note}
                    </span>
                  </div>
                )}
              </div>
            </CCol>
            <CCol lg={4} className="order-summary-section">
              <div className="summary-item">
                <label className="summary-label">{t('quantity')}</label>
                <div className="summary-value">{formatQuantity(currentOrder.ProductQuantity)}</div>
                {currentOrder.UnitName && (
                  <div className="summary-unit">{currentOrder.UnitName}</div>
                )}
              </div>
              <div className="summary-item">
                <label className="summary-label">
                  <CIcon icon={cilCash} className="me-1" />
                  {t('costPrice')}
                  {costCurrencySymbol && (
                    <span className="currency-indicator"> ({costCurrencySymbol})</span>
                  )}
                </label>
                <div className="summary-value cost-price">
                  {formatPrice(currentOrder.ProductCostPrice, costCurrencyGuid, currentOrder.CostCurrencyName)}
                </div>
              </div>
              <div className="summary-item">
                <label className="summary-label">
                  <CIcon icon={cilCart} className="me-1" />
                  {t('salesPrice')}
                  <span className="currency-indicator"> ({salesCurrencySymbol})</span>
                </label>
                <div className="summary-value sales-price">
                  {formatPrice(currentOrder.ProductSalesPrice, salesCurrencyGuid, currentOrder.SalesCurrencyName)}
                </div>
              </div>
              <div className="summary-item total">
                <label className="summary-label">
                  {t('totalPrice')}
                  <span className="currency-indicator"> ({salesCurrencySymbol})</span>
                </label>
                <div className="summary-value total-price">
                  {formatPrice(currentOrder.ProductTotalPrice, salesCurrencyGuid, currentOrder.SalesCurrencyName)}
                </div>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* Tabs */}
      <CCard className="order-details-tabs">
        <CCardHeader className="tabs-header">
          <CNav variant="tabs" role="tablist" className="custom-tabs">
            <CNavItem>
              <CNavLink
                active={activeTab === 'product'}
                onClick={() => setActiveTab('product')}
                className="custom-tab-link"
              >
                <CIcon icon={cilLayers} className="me-2" />
                {t('productInfo') || 'Ürün Bilgileri'}
                {productCount > 0 && (
                  <CBadge color="info" className="ms-2">{productCount}</CBadge>
                )}
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink
                active={activeTab === 'status'}
                onClick={() => setActiveTab('status')}
                className="custom-tab-link"
              >
                <CIcon icon={cilTag} className="me-2" />
                {t('statusInfo') || 'Durum Bilgileri'}
                {orderStatusCount > 0 && (
                  <CBadge color="info" className="ms-2">{orderStatusCount}</CBadge>
                )}
              </CNavLink>
            </CNavItem>
            {shipmentCount > 0 && (
              <CNavItem>
                <CNavLink
                  active={activeTab === 'shipment'}
                  onClick={() => setActiveTab('shipment')}
                  className="custom-tab-link"
                >
                  <CIcon icon={cilTruck} className="me-2" />
                  {t('shipmentInfo') || 'Sevkiyat Bilgileri'}
                  <CBadge color="info" className="ms-2">{shipmentCount}</CBadge>
                </CNavLink>
              </CNavItem>
            )}
            {stockCount > 0 && (
              <CNavItem>
                <CNavLink
                  active={activeTab === 'stock'}
                  onClick={() => setActiveTab('stock')}
                  className="custom-tab-link"
                >
                  <CIcon icon={cilHouse} className="me-2" />
                  {t('stockInfo') || 'Stok Bilgileri'}
                  <CBadge color="info" className="ms-2">{stockCount}</CBadge>
                </CNavLink>
              </CNavItem>
            )}
          </CNav>
        </CCardHeader>
        
        <CCardBody className="tabs-content">
          <CTabContent>
            {/* Product Information Tab */}
            <CTabPane visible={activeTab === 'product'}>
              {productDetail ? (
                <div className="product-container">
                  <div className="product-header">
                    <h5>{t('productDetails') || 'Ürün Detayları'}</h5>
                  </div>
                  <CRow>
                    <CCol lg={6}>
                      <CCard className="info-card">
                        <CCardHeader className="info-card-header">
                          <CIcon icon={cilLayers} className="me-2" />
                          {t('productInfo') || 'Ürün Bilgileri'}
                        </CCardHeader>
                        <CCardBody>
                          <div className="info-grid">
                            <div className="info-item">
                              <label className="info-label">{t('productId') || 'Ürün ID'}</label>
                              <div className="info-value">
                                <CBadge color="primary" shape="rounded-pill" className="system-badge">
                                  {productDetail.PrdProductGuid}
                                </CBadge>
                              </div>
                            </div>
                            <div className="info-item">
                              <label className="info-label">{t('weaveCode') || 'Dokuma Kodu'}</label>
                              <div className="info-value">{productDetail.WeaveCode || '-'}</div>
                            </div>
                            <div className="info-item">
                              <label className="info-label">{t('materialName') || 'Malzeme Adı'}</label>
                              <div className="info-value">{productDetail.MaterialName || '-'}</div>
                            </div>
                            <div className="info-item">
                              <label className="info-label">{t('insertDatetime') || 'Oluşturma Tarihi'}</label>
                              <div className="info-value">
                                <CIcon icon={cilCalendar} className="me-2 text-muted" />
                                {formatInsertDatetime(productDetail.InsertDatetime)}
                              </div>
                            </div>
                          </div>
                        </CCardBody>
                      </CCard>
                    </CCol>
                    
                    <CCol lg={6}>
                      <CCard className="info-card">
                        <CCardHeader className="info-card-header">
                          <CIcon icon={cilDescription} className="me-2" />
                          {t('certificateAndLot') || 'Sertifika ve Lot Bilgileri'}
                        </CCardHeader>
                        <CCardBody>
                          <div className="info-grid">
                            <div className="info-item">
                              <label className="info-label">{t('certificate') || 'Sertifika'}</label>
                              <div className="info-value">{productDetail.Certificate || '-'}</div>
                            </div>
                            {productDetail.Lot && String(productDetail.Lot).trim() !== '' ? (
                              <div className="info-item">
                                <label className="info-label">{t('lot') || 'Lot'}</label>
                                <div className="info-value">{productDetail.Lot}</div>
                              </div>
                            ) : null}
                          </div>
                        </CCardBody>
                        </CCard>
                   </CCol>
                 </CRow>
               </div>
             ) : (
               <div className="empty-state">
                 <CIcon icon={cilLayers} size="3xl" className="empty-icon" />
                 <h5>{t('noProductDetailsFound') || 'Ürün Detayları Bulunamadı'}</h5>
                 <p className="text-muted">
                   {t('noProductDetailsDescription') || 'Bu sipariş için ürün detayları bulunmuyor.'}
                 </p>
               </div>
             )}
           </CTabPane>

           {/* Status Information Tab */}
           <CTabPane visible={activeTab === 'status'}>
             {orderStatusDetail ? (
               <div className="status-container">
                 <div className="status-header">
                   <h5>{t('orderStatusDetails') || 'Sipariş Durum Detayları'}</h5>
                 </div>
                 <CRow>
                   <CCol lg={12}>
                     <CCard className="info-card">
                       <CCardHeader className="info-card-header">
                         <CIcon icon={cilTag} className="me-2" />
                         {t('statusInfo') || 'Durum Bilgileri'}
                       </CCardHeader>
                       <CCardBody>
                         <div className="info-grid">
                           <div className="info-item">
                             <label className="info-label">{t('statusName') || 'Durum Adı'}</label>
                             <div className="info-value">{orderStatusDetail.Name || '-'}</div>
                           </div>
                           {orderStatusDetail.Description && String(orderStatusDetail.Description).trim() !== '' ? (
                             <div className="info-item">
                               <label className="info-label">{t('statusDescription') || 'Durum Açıklaması'}</label>
                               <div className="info-value">{orderStatusDetail.Description}</div>
                             </div>
                           ) : null}
                         </div>
                       </CCardBody>
                     </CCard>
                   </CCol>
                 </CRow>
               </div>
             ) : (
               <div className="empty-state">
                 <CIcon icon={cilTag} size="3xl" className="empty-icon" />
                 <h5>{t('noStatusDetailsFound') || 'Durum Detayları Bulunamadı'}</h5>
                 <p className="text-muted">
                   {t('noStatusDetailsDescription') || 'Bu sipariş için durum detayları bulunmuyor.'}
                 </p>
               </div>
             )}
           </CTabPane>

           {/* Shipment Information Tab */}
          {shipmentCount > 0 && (
          <CTabPane visible={activeTab === 'shipment'}>
            {filteredShipmentDetails.length > 0 ? (
               <div className="shipment-container">
                 <div className="shipment-header">
                   <h5>{t('shipmentDetails') || 'Sevkiyat Detayları'}</h5>
                   <p className="text-muted">
                     {t('shipmentCount') || 'Toplam'}: {filteredShipmentDetails.length} {t('shipment') || 'sevkiyat'}
                   </p>
                 </div>
                 <CCard className="info-card">
                   <CCardHeader className="info-card-header">
                     <CIcon icon={cilTruck} className="me-2" />
                     {t('shipmentList') || 'Sevkiyat Listesi'}
                   </CCardHeader>
                   <CCardBody>
                     <CTable responsive striped hover className="shipment-table">
                       <CTableHead>
                         <CTableRow>
                           <CTableHeaderCell>{t('shipmentDate') || 'Sevkiyat Tarihi'}</CTableHeaderCell>
                           <CTableHeaderCell>{t('deliveryNo') || 'Teslimat No'}</CTableHeaderCell>
                           <CTableHeaderCell>{t('billingCompany') || 'Fatura Şirketi'}</CTableHeaderCell>
                           <CTableHeaderCell>{t('status') || 'Durum'}</CTableHeaderCell>
                           <CTableHeaderCell>{t('unit') || 'Birim'}</CTableHeaderCell>
                           <CTableHeaderCell>{t('quantity') || 'Miktar'}</CTableHeaderCell>
                           <CTableHeaderCell>{tr('rollCount', 'Top Sayısı')}</CTableHeaderCell>
                           {showShipmentLotCol ? (
                             <CTableHeaderCell>{t('lot') || 'Lot'}</CTableHeaderCell>
                           ) : null}
                           {showShipmentDescriptionCol ? (
                             <CTableHeaderCell>{t('description') || 'Açıklama'}</CTableHeaderCell>
                           ) : null}
                           <CTableHeaderCell>{tr('transferToStock', 'Stoğa Aktarım')}</CTableHeaderCell>
                         </CTableRow>
                       </CTableHead>
                       <CTableBody>
                         {filteredShipmentDetails.map((shipment, index) => (
                           <CTableRow key={index}>
                             <CTableDataCell>
                               <CIcon icon={cilCalendar} className="me-2 text-muted" />
                               {formatDate(shipment.ShipmentDatetime)}
                             </CTableDataCell>
                             <CTableDataCell>{shipment.DeliveryNo || '-'}</CTableDataCell>
                             <CTableDataCell>{shipment.BillingCompany || '-'}</CTableDataCell>
                             <CTableDataCell>
                               {getShipmentStatusBadge(shipment.ShipmentStatusName)}
                             </CTableDataCell>
                             <CTableDataCell>{shipment.ShipmentPrdUnitName || '-'}</CTableDataCell>
                             <CTableDataCell>
                               <CBadge color="info" shape="rounded-pill">
                                 {formatScaledAmount(shipment.ProductShippedQuantity) || '-'}
                               </CBadge>
                             </CTableDataCell>
                             <CTableDataCell>
                               <CBadge color="info" shape="rounded-pill">
                                 {shipment.RollCount ?? 0}
                               </CBadge>
                             </CTableDataCell>
                             {showShipmentLotCol ? (
                               <CTableDataCell>{shipment.Lot || '-'}</CTableDataCell>
                             ) : null}
                             {showShipmentDescriptionCol ? (
                               <CTableDataCell>{shipment.Description || '-'}</CTableDataCell>
                             ) : null}
                             <CTableDataCell>
                               <CButton
                                 color="primary"
                                 variant="outline"
                                 size="sm"
                                 onClick={() => openShipmentSwapModal(shipment)}
                               >
                                 <CIcon icon={cilPencil} className="me-1" />
                                 {tr('transferToStock', 'Stoğa Aktarım')}
                               </CButton>
                             </CTableDataCell>
                           </CTableRow>
                         ))}
                       </CTableBody>
                     </CTable>
                   </CCardBody>
                 </CCard>
                 <div className="detail-totals-line">
                   {shipmentQuantityTotals.map(({ unitName, quantity }) => (
                     <div className="detail-total-item" key={unitName.toLocaleLowerCase('tr-TR')}>
                       <span className="detail-total-label">{unitName}:</span>
                       <strong>{formatScaledAmount(quantity)}</strong>
                     </div>
                   ))}
                   <div className="detail-total-item detail-total-roll">
                     <span className="detail-total-label">{tr('rollCount', 'Top Sayısı')}:</span>
                     <strong>{shipmentRollTotal}</strong>
                   </div>
                 </div>
               </div>
            ) : null}
          </CTabPane>
          )}

           {/* Stock Information Tab */}
          {stockCount > 0 && (
          <CTabPane visible={activeTab === 'stock'}>
            {filteredStockDetails.length > 0 ? (
               <div className="stock-container">
                 <div className="stock-header">
                   <h5>{t('stockDetails') || 'Stok Detayları'}</h5>
                   <p className="text-muted">
                     {t('stockCount') || 'Toplam'}: {filteredStockDetails.length} {t('stock') || 'stok kaydı'}
                   </p>
                 </div>
                 <CCard className="info-card">
                   <CCardHeader className="info-card-header">
                     <CIcon icon={cilHouse} className="me-2" />
                     {t('stockList') || 'Stok Listesi'}
                   </CCardHeader>
                   <CCardBody>
                     <CTable responsive striped hover className="stock-table">
                       <CTableHead>
                         <CTableRow>
                           <CTableHeaderCell>{t('unit') || 'Birim'}</CTableHeaderCell>
                           <CTableHeaderCell>{t('status') || 'Durum'}</CTableHeaderCell>
                           <CTableHeaderCell>{t('quantity') || 'Miktar'}</CTableHeaderCell>
                           <CTableHeaderCell>{tr('rollCount', 'Top Sayısı')}</CTableHeaderCell>
                           <CTableHeaderCell>{t('stockPlace') || 'Stok Yeri'}</CTableHeaderCell>
                           {showStockLotCol ? (
                             <CTableHeaderCell>{t('lot') || 'Lot'}</CTableHeaderCell>
                           ) : null}
                           {showStockDescriptionCol ? (
                             <CTableHeaderCell>{t('description') || 'Açıklama'}</CTableHeaderCell>
                           ) : null}
                           <CTableHeaderCell>{t('insertDate') || 'Oluşturma Tarihi'}</CTableHeaderCell>
                           <CTableHeaderCell>{tr('transferToShipment', 'Sevkiyata Aktarım')}</CTableHeaderCell>
                         </CTableRow>
                       </CTableHead>
                       <CTableBody>
                         {filteredStockDetails.map((stock, index) => (
                           <CTableRow key={index}>
                             <CTableDataCell>{stock.UnitName || '-'}</CTableDataCell>
                             <CTableDataCell>
                               {getStockStatusBadge(stock.Name)}
                             </CTableDataCell>
                             <CTableDataCell>
                               <CBadge color="success" shape="rounded-pill">
                                 {formatScaledAmount(stock.ProductStockQuantity) || '-'}
                               </CBadge>
                             </CTableDataCell>
                             <CTableDataCell>
                               <CBadge color="info" shape="rounded-pill">
                                 {stock.RollCount ?? 0}
                               </CBadge>
                             </CTableDataCell>
                             <CTableDataCell>
                               <CIcon icon={cilLocationPin} className="me-2 text-muted" />
                               {stock.StockPlace || '-'}
                             </CTableDataCell>
                             {showStockLotCol ? (
                               <CTableDataCell>{stock.Lot || '-'}</CTableDataCell>
                             ) : null}
                             {showStockDescriptionCol ? (
                               <CTableDataCell>{stock.Description || '-'}</CTableDataCell>
                             ) : null}
                             <CTableDataCell>
                               <CIcon icon={cilCalendar} className="me-2 text-muted" />
                               {formatInsertDatetime(stock.InsertDatetime)}
                             </CTableDataCell>
                             <CTableDataCell>
                               <CButton
                                 color="primary"
                                 variant="outline"
                                 size="sm"
                                 onClick={() => openStockSwapModal(stock)}
                               >
                                 <CIcon icon={cilPencil} className="me-1" />
                                 {tr('transferToShipment', 'Sevkiyata Aktarım')}
                               </CButton>
                             </CTableDataCell>
                           </CTableRow>
                         ))}
                       </CTableBody>
                     </CTable>
                   </CCardBody>
                 </CCard>
                 <div className="detail-totals-line">
                   {stockQuantityTotals.map(({ unitName, quantity }) => (
                     <div className="detail-total-item" key={unitName.toLocaleLowerCase('tr-TR')}>
                       <span className="detail-total-label">{unitName}:</span>
                       <strong>{formatScaledAmount(quantity)}</strong>
                     </div>
                   ))}
                   <div className="detail-total-item detail-total-roll">
                     <span className="detail-total-label">{tr('rollCount', 'Top Sayısı')}:</span>
                     <strong>{stockRollTotal}</strong>
                   </div>
                 </div>
               </div>
            ) : null}
          </CTabPane>
          )}
         </CTabContent>
       </CCardBody>
     </CCard>

      <CModal className="swap-modal" visible={shipmentSwapModalVisible} onClose={() => setShipmentSwapModalVisible(false)} alignment="center">
        <CModalHeader>
          <CModalTitle>{tr('updateShipmentTransfer', 'Sevkiyattan Stoğa Aktarım')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div className="swap-modal-summary-grid mb-3">
            <div className="swap-modal-summary">
              <div className="swap-modal-summary-title">{tr('sourceShipment', 'Kaynak Sevkiyat')}</div>
              <CFormInput
                type="text"
                disabled
                className="swap-modal-readonly-field"
                value={selectedShipmentRow
                  ? `${selectedShipmentRow.BillingCompany || '-'} • ${tr('quantity', 'Miktar')}: ${formatScaledAmount(selectedShipmentRow.ProductShippedQuantity) || '0,00'} • ${tr('rollCount', 'Top')}: ${selectedShipmentRow.RollCount || 0}`
                  : '-'}
              />
            </div>
          </div>

          <div className="mb-3">
            <CFormLabel>{tr('targetStock', 'Hedef Stok')}</CFormLabel>
            <CFormSelect
              value={shipmentSwapForm.StockId}
              onChange={(e) => setShipmentSwapForm((prev) => ({ ...prev, StockId: e.target.value }))}
              disabled={stockTargetOptions.length === 0}
            >
              <option value="" disabled>
                {stockTargetOptions.length > 0
                  ? tr('selectStock', 'Stok seçiniz')
                  : tr('noStockRecord', 'Uygun stok kaydı bulunamadı')}
              </option>
              {stockTargetOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </CFormSelect>
          </div>
          <div className="mb-3">
            <CFormLabel>{tr('transferQuantity', 'Aktarılacak Miktar')}</CFormLabel>
            <CFormInput
              type="number"
              min="0"
              step="1"
              value={shipmentSwapForm.ShipmentSwapQuantity}
              onChange={(e) => setShipmentSwapForm((prev) => ({ ...prev, ShipmentSwapQuantity: e.target.value }))}
              placeholder={tr('enterTransferQuantity', 'Aktarılacak miktarı girin')}
            />
          </div>
          <div>
            <CFormLabel>{tr('transferRollCount', 'Aktarılacak Top Sayısı')}</CFormLabel>
            <CFormInput
              type="number"
              min="0"
              value={shipmentSwapForm.ShipmentSwapRollCount}
              onChange={(e) => setShipmentSwapForm((prev) => ({ ...prev, ShipmentSwapRollCount: e.target.value }))}
              placeholder={tr('enterTransferRollCount', 'Aktarılacak top sayısını girin')}
            />
          </div>

          <div className="swap-modal-help mt-2">{tr('transferRuleHint', 'En az bir alana 0’dan büyük değer girilmelidir.')}</div>
        </CModalBody>
        <CModalFooter className="swap-modal-footer">
          <CButton color="secondary" onClick={() => setShipmentSwapModalVisible(false)}>
            {t('cancel') || 'İptal'}
          </CButton>
          <CButton color="primary" onClick={handleShipmentSwapSubmit} disabled={isShipmentSwapDisabled}>
            {t('confirm') || 'Onayla'}
          </CButton>
        </CModalFooter>
      </CModal>

      <CModal className="swap-modal" visible={stockSwapModalVisible} onClose={() => setStockSwapModalVisible(false)} alignment="center">
        <CModalHeader>
          <CModalTitle>{tr('updateStockTransfer', 'Stoktan Sevkiyata Aktarım')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div className="swap-modal-summary-grid mb-3">
            <div className="swap-modal-summary">
              <div className="swap-modal-summary-title">{tr('sourceStock', 'Kaynak Stok')}</div>
              <CFormInput
                type="text"
                disabled
                className="swap-modal-readonly-field"
                value={selectedStockRow
                  ? `${selectedStockRow.StockPlace || '-'} • ${tr('quantity', 'Miktar')}: ${formatScaledAmount(selectedStockRow.ProductStockQuantity) || '0,00'} • ${tr('rollCount', 'Top')}: ${selectedStockRow.RollCount || 0}`
                  : '-'}
              />
            </div>
          </div>

          <div className="mb-3">
            <CFormLabel>{tr('targetShipment', 'Hedef Sevkiyat')}</CFormLabel>
            <CFormSelect
              value={stockSwapForm.ShipmentId}
              onChange={(e) => setStockSwapForm((prev) => ({ ...prev, ShipmentId: e.target.value }))}
              disabled={shipmentTargetOptions.length === 0}
            >
              <option value="" disabled>
                {shipmentTargetOptions.length > 0
                  ? tr('selectShipment', 'Sevkiyat seçiniz')
                  : tr('noShipmentRecord', 'Uygun sevkiyat kaydı bulunamadı')}
              </option>
              {shipmentTargetOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </CFormSelect>
          </div>
          <div className="mb-3">
            <CFormLabel>{tr('transferQuantity', 'Aktarılacak Miktar')}</CFormLabel>
            <CFormInput
              type="number"
              min="0"
              step="1"
              value={stockSwapForm.StockSwapQuantity}
              onChange={(e) => setStockSwapForm((prev) => ({ ...prev, StockSwapQuantity: e.target.value }))}
              placeholder={tr('enterTransferQuantity', 'Aktarılacak miktarı girin')}
            />
          </div>
          <div>
            <CFormLabel>{tr('transferRollCount', 'Aktarılacak Top Sayısı')}</CFormLabel>
            <CFormInput
              type="number"
              min="0"
              value={stockSwapForm.StockSwapRollCount}
              onChange={(e) => setStockSwapForm((prev) => ({ ...prev, StockSwapRollCount: e.target.value }))}
              placeholder={tr('enterTransferRollCount', 'Aktarılacak top sayısını girin')}
            />
          </div>

          <div className="swap-modal-help mt-2">{tr('transferRuleHint', 'En az bir alana 0’dan büyük değer girilmelidir.')}</div>
        </CModalBody>
        <CModalFooter className="swap-modal-footer">
          <CButton color="secondary" onClick={() => setStockSwapModalVisible(false)}>
            {t('cancel') || 'İptal'}
          </CButton>
          <CButton color="primary" onClick={handleStockSwapSubmit} disabled={isStockSwapDisabled}>
            {t('confirm') || 'Onayla'}
          </CButton>
        </CModalFooter>
      </CModal>
   </div>
 );
}

const mapStateToProps = (state) => {
 return {
   orderDetailsData: state.orderReducer?.orderDetailsData || null,
   orderDetailsError: state.orderReducer?.orderDetailsError || null,
   isLoading: state.orderReducer?.isLoading || false,
   currencyList: state.currencyReducer?.currencyList || [],
 };
};

const mapDispatchToProps = (dispatch) => {
 return {
   getOrderDetailsRequest: (payload) => {
     dispatch(getOrderDetailsRequest(payload));
   },
   updateSwapStockRequest: (payload) => {
     dispatch(updateSwapStockRequest(payload));
   },
   updateSwapShipmentRequest: (payload) => {
     dispatch(updateSwapShipmentRequest(payload));
   },
   getCurrencyRequestAction: (payload) => {
     dispatch(getCurrencyRequestAction(payload));
   },
 };
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderDetails);
